import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import * as eventApis from 'apis/events';
import { getBookingState, getDisplayState } from './eventState';
import './EventListStatic.scss';

/*
  Event Listing layout, converted from the live-test.html mockup and built on
  the same card shape as GiveawayListStatic so the two listings read as one
  family.

  Pluggable, self-contained component:
  - All CSS is scoped under ".ev-page" with "ev-" prefixed classes, so it can
    be dropped into the public site or the Dashboard without colliding with
    existing global styles.
  - Renders real event data from the API. Empty fields/sections are omitted
    (no static content fallbacks).
  - On the public site it fetches public + published events; inside the
    dashboard it fetches the logged-in member's list (which comes back with
    isRegistered / isWaitlisted / participantCount) and additionally renders
    the member's own registration state under each card.
  - Only listing-worthy fields are used. description, images[], features,
    youtubeLink, termsAndConditions and drawDate stay on the details page.
*/

const STATE_META = {
  active: { label: 'ACTIVE', className: '' },
  upcoming: { label: 'UPCOMING', className: 'ev-tag-upcoming' },
  past: { label: 'PAST', className: 'ev-tag-past' },
};

const TYPE_META = {
  standard: {
    label: 'AUTO ENTRY FOR MEMBERS',
    className: 'ev-auto',
    icon: 'fa-solid fa-bolt',
  },
  registration: {
    label: 'REGISTRATION REQUIRED',
    className: 'ev-register',
    icon: 'fa-solid fa-pen-to-square',
  },
  external: {
    label: 'EXTERNAL EVENT',
    className: 'ev-external',
    icon: 'fa-solid fa-arrow-up-right-from-square',
  },
};

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
];

const formatDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })} IST`;
};

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const eventTime = (event) => toDate(event.eventDate)?.getTime() ?? 0;

const EventListStatic = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const assetUrl = process.env.REACT_APP_TAURIAN_ASSET_URL;
  const isDashboard = location.pathname.startsWith('/dashboard');

  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = isDashboard
          ? await eventApis.fetchUserEvents()
          : await eventApis.fetchPublicEvents();
        setEvents(response.data || []);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, [isDashboard]);

  // The API returns events oldest first. Current ones keep that order;
  // past ones go after them, most recently ended first.
  const visibleEvents = useMemo(() => {
    const withState = events.map((event) => ({ event, state: getDisplayState(event) }));
    const current = withState.filter(({ state }) => state !== 'past');
    const past = withState
      .filter(({ state }) => state === 'past')
      .sort((a, b) => eventTime(b.event) - eventTime(a.event));

    return [...current, ...past]
      .filter(({ state }) => tab === 'all' || state === tab)
      .map(({ event }) => event);
  }, [events, tab]);

  // Public visitors have no event pages of their own - the details page lives
  // in the dashboard, so they are sent through login and dropped back on it.
  const handlePublicCta = (id) => {
    const target = `/dashboard/events/${id}`;
    if (localStorage.getItem('token')) {
      navigate(target);
      return;
    }
    sessionStorage.setItem('postLoginRedirect', target);
    navigate('/Login');
  };

  return (
    <div className="ev-page">
      <div className="ev-container">

        <div className="ev-head">
          <h2>Current <span className="ev-accent">Events</span></h2>
          <p className="ev-lede">
            From community rides and meet-ups to track days, training and
            exclusive experiences, Taurian brings you closer to what’s happening
            around you. Members unlock discounted access, free entries and
            specially funded experiences which were once out of reach.
          </p>

          {events.length > 0 && (
            <div className="ev-filter-bar">
              <div className="ev-tabs">
                {TABS.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`ev-tab ${tab === item.id ? 'ev-tab-active' : ''}`}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <span className="ev-count">
                Showing <strong>{visibleEvents.length}</strong> of {events.length} events
              </span>
            </div>
          )}
        </div>

        <div className="ev-list">
          {events.length === 0 && (
            <div className="ev-empty">No events to show right now.</div>
          )}

          {events.length > 0 && visibleEvents.length === 0 && (
            <div className="ev-empty">No events in this category.</div>
          )}

          {visibleEvents.map((event) => {
            const displayState = getDisplayState(event);
            const stateMeta = STATE_META[displayState];
            const typeMeta = TYPE_META[event.type];
            const isPast = displayState === 'past';
            const bookingState = getBookingState(event);

            const title = [event.firstName, event.lastName].filter(Boolean);
            const description = event.shortDescription || event.description;
            const imagePath = event.image || event.images?.[0] || event.headerImage;
            const image = imagePath && assetUrl ? `${assetUrl}${imagePath}` : '';

            const eventDate = toDate(event.eventDate);
            const openDate = toDate(event.registrationOpenDate);
            const closeDate = toDate(event.registrationCloseDate);

            // Closed or drawn events that have not happened yet stay in Active,
            // so the side column says why they can no longer be joined.
            // Finished events get no entry block at all.
            let entryBlock = null;
            if (!isPast && event.status === 'completed' && event.type !== 'external') {
              entryBlock = { label: 'DRAW', value: 'Completed', sub: 'Winners selected' };
            } else if (!isPast && event.type === 'registration') {
              if (event.status === 'closed') {
                entryBlock = { label: 'REGISTRATION', value: 'Closed', sub: 'No longer accepting bookings' };
              } else if (bookingState === 'not-open' && openDate) {
                entryBlock = { label: 'BOOKING OPENS', date: openDate };
              } else if (bookingState === 'closed' && closeDate) {
                entryBlock = { label: 'BOOKING CLOSED', date: closeDate };
              } else if (closeDate) {
                entryBlock = { label: 'BOOKING CLOSES', date: closeDate };
              }
            } else if (!isPast && event.type === 'standard') {
              entryBlock = { label: 'ENTRY', value: 'Automatic', sub: 'No booking needed', muted: true };
            }

            const stats = [];
            if (event.location) {
              stats.push({
                icon: 'fa-solid fa-location-dot',
                value: event.location,
                label: 'Location',
              });
            }
            if (event.maxParticipants != null) {
              // participantCount only comes back on the dashboard endpoint.
              stats.push({
                icon: 'fa-solid fa-user-group',
                value:
                  event.participantCount != null
                    ? `${event.participantCount} / ${event.maxParticipants}`
                    : `${event.maxParticipants}`,
                label:
                  event.participantCount == null
                    ? 'Capacity'
                    : isPast
                    ? 'Attended'
                    : 'Slots filled',
              });
            }
            if (event.winnerCount > 1) {
              stats.push({
                icon: 'fa-solid fa-trophy',
                value: event.winnerCount,
                label: 'Winners',
              });
            }

            const showState = isDashboard && (event.isRegistered || event.isWaitlisted);

            return (
              <article className={`ev-card ${isPast ? 'ev-past' : ''}`} key={event.id}>

                <div className="ev-row">

                  <div className="ev-card-img">
                    {stateMeta && (
                      <span className={`ev-tag ${stateMeta.className}`}>{stateMeta.label}</span>
                    )}
                    {image && <img src={image} alt={title.join(' ')} />}
                  </div>

                  <div className="ev-card-body">
                    {typeMeta && (
                      <span className={`ev-category ${typeMeta.className}`}>
                        <i className={typeMeta.icon}></i>{typeMeta.label}
                      </span>
                    )}

                    {title.length > 0 && (
                      <h3>
                        {title[0]}
                        {title[1] && <> <span className="ev-accent">{title[1]}</span></>}
                      </h3>
                    )}

                    {event.tagline && <span className="ev-tagline">{event.tagline}</span>}
                    {description && <p>{description}</p>}

                    {stats.length > 0 && (
                      <div className="ev-stats">
                        {stats.slice(0, 3).map((stat) => (
                          <div className="ev-stat" key={stat.label}>
                            <i className={stat.icon}></i>
                            <strong>{stat.value}</strong>
                            <span>{stat.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="ev-card-side">
                    {eventDate && (
                      <div className="ev-date-block">
                        <div className={`ev-date-label ${isPast ? 'ev-muted' : 'ev-when'}`}>
                          <i className="fa-solid fa-calendar-days"></i> EVENT DATE
                        </div>
                        <div className="ev-date-value">{formatDate(eventDate)}</div>
                        <div className="ev-date-time">{formatTime(eventDate)}</div>
                      </div>
                    )}

                    {entryBlock && (
                      <div className="ev-date-block">
                        <div className={`ev-date-label ${entryBlock.muted ? 'ev-muted' : 'ev-entry'}`}>
                          <i className={`fa-solid ${entryBlock.muted ? 'fa-user-check' : 'fa-clock'}`}></i>{' '}
                          {entryBlock.label}
                        </div>
                        <div className="ev-date-value">
                          {entryBlock.date ? formatDate(entryBlock.date) : entryBlock.value}
                        </div>
                        <div className="ev-date-time">
                          {entryBlock.date ? formatTime(entryBlock.date) : entryBlock.sub}
                        </div>
                      </div>
                    )}

                    {isDashboard ? (
                      <Link
                        to={`/dashboard/events/${event.id}`}
                        className={`ev-view-btn ${isPast ? 'ev-ghost' : ''}`}
                      >
                        VIEW DETAILS <i className="fa-solid fa-arrow-right"></i>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className={`ev-view-btn ${isPast ? 'ev-ghost' : ''}`}
                        onClick={() => handlePublicCta(event.id)}
                      >
                        VIEW DETAILS <i className="fa-solid fa-arrow-right"></i>
                      </button>
                    )}
                  </div>

                </div>

                {showState && (
                  <div className="ev-extra">
                    {event.isRegistered && (
                      <span className="ev-state ev-in">
                        <i className="fa-solid fa-circle-check"></i>
                        {isPast ? 'Attended' : "You're registered"}
                      </span>
                    )}

                    {event.isWaitlisted && (
                      <>
                        <span className="ev-state ev-wait">
                          <i className="fa-solid fa-hourglass-half"></i>Waitlisted
                        </span>
                        {event.waitlistCount > 0 && (
                          <span className="ev-extra-meta">
                            <i className="fa-solid fa-users-line"></i>
                            {event.waitlistCount} on the waiting list
                          </span>
                        )}
                      </>
                    )}

                    {!isPast && (
                      <Link to={`/dashboard/events/${event.id}`} className="ev-extra-link">
                        Manage registration <i className="fa-solid fa-arrow-right"></i>
                      </Link>
                    )}
                  </div>
                )}

              </article>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default EventListStatic;
